import React, { useEffect, useRef, useState, useCallback } from 'react';

const PICO8Emulator: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [availableCartridges, setAvailableCartridges] = useState<string[]>([]);
  const [selectedCartridge, setSelectedCartridge] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [code, setCode] = useState<string>('-- PICO-8 Style Fantasy Console\n-- Write your game code here!\n\nfunction _init()\n  x = 64\n  y = 64\nend\n\nfunction _update()\n  if btn(0) then x -= 1 end\n  if btn(1) then x += 1 end\n  if btn(2) then y -= 1 end\n  if btn(3) then y += 1 end\nend\n\nfunction _draw()\n  cls()\n  print("FANTASY CONSOLE", 10, 10, 7)\n  print("Use arrow keys to move", 10, 20, 6)\n  circfill(x, y, 4, 8)\nend');

  // PICO-8 color palette (16 colors)
  const palette = [
    '#000000', '#1D2B53', '#7E2553', '#008751',
    '#AB5236', '#5F574F', '#C2C3C7', '#FFF1E8',
    '#FF004D', '#FFA300', '#FFEC27', '#00E436',
    '#29ADFF', '#83769C', '#FF77A8', '#FFCCAA'
  ];

  // Game state
  const gameStateRef = useRef({
    x: 64,
    y: 64,
    buttons: [false, false, false, false, false, false] // left, right, up, down, z, x
  });

  useEffect(() => {
    fetchAvailableCartridges();
    setupKeyboardControls();
  }, []);

  const fetchAvailableCartridges = async () => {
    try {
      const response = await fetch('/api/roms/pico8');
      const data = await response.json();
      setAvailableCartridges(data.files || []);
    } catch (error) {
      console.error('Failed to fetch cartridges:', error);
      setAvailableCartridges([]);
    }
  };

  const setupKeyboardControls = () => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const keyMap: { [key: string]: number } = {
        'ArrowLeft': 0,
        'ArrowRight': 1,
        'ArrowUp': 2,
        'ArrowDown': 3,
        'KeyZ': 4,
        'KeyX': 5
      };

      const buttonIndex = keyMap[e.code];
      if (buttonIndex !== undefined) {
        gameStateRef.current.buttons[buttonIndex] = true;
        e.preventDefault();
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      const keyMap: { [key: string]: number } = {
        'ArrowLeft': 0,
        'ArrowRight': 1,
        'ArrowUp': 2,
        'ArrowDown': 3,
        'KeyZ': 4,
        'KeyX': 5
      };

      const buttonIndex = keyMap[e.code];
      if (buttonIndex !== undefined) {
        gameStateRef.current.buttons[buttonIndex] = false;
        e.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  };

  // Simple fantasy console API
  const fantasyAPI = {
    cls: (color = 0) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      
      ctx.fillStyle = palette[color];
      ctx.fillRect(0, 0, 128, 128);
    },
    
    print: (text: string, x: number, y: number, color = 7) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      
      ctx.fillStyle = palette[color];
      ctx.font = '8px monospace';
      ctx.fillText(text, x, y);
    },
    
    circfill: (x: number, y: number, r: number, color = 7) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      
      ctx.fillStyle = palette[color];
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    },
    
    btn: (buttonIndex: number) => {
      return gameStateRef.current.buttons[buttonIndex] || false;
    }
  };

  const gameLoop = useCallback(() => {
    if (!isRunning) return;

    try {
      // Simple update logic (simulating the demo)
      const state = gameStateRef.current;
      
      if (state.buttons[0]) state.x -= 1; // left
      if (state.buttons[1]) state.x += 1; // right
      if (state.buttons[2]) state.y -= 1; // up
      if (state.buttons[3]) state.y += 1; // down
      
      // Wrap around screen
      if (state.x < 0) state.x = 127;
      if (state.x > 127) state.x = 0;
      if (state.y < 0) state.y = 127;
      if (state.y > 127) state.y = 0;

      // Draw
      fantasyAPI.cls(1); // Dark blue background
      fantasyAPI.print('FANTASY CONSOLE', 10, 15, 7);
      fantasyAPI.print('Use arrow keys to move', 5, 25, 6);
      fantasyAPI.print('Z/X for action buttons', 5, 35, 6);
      fantasyAPI.circfill(state.x, state.y, 4, 8); // Pink circle

      requestAnimationFrame(gameLoop);
    } catch (error) {
      console.error('Game loop error:', error);
      setIsRunning(false);
    }
  }, [isRunning]);

  const runCartridge = () => {
    if (!canvasRef.current) return;
    
    try {
      setError('');
      gameStateRef.current = { x: 64, y: 64, buttons: [false, false, false, false, false, false] };
      setIsRunning(true);
      gameLoop();
    } catch (error) {
      setError(`Error running cartridge: ${error}`);
      console.error('Cartridge error:', error);
    }
  };

  const stopCartridge = () => {
    setIsRunning(false);
  };

  useEffect(() => {
    if (isRunning) {
      gameLoop();
    }
  }, [isRunning, gameLoop]);

  return (
    <div className="pico8-emulator">
      <div className="pico8-controls" style={{ marginBottom: '10px' }}>
        <select 
          value={selectedCartridge} 
          onChange={(e) => setSelectedCartridge(e.target.value)}
          disabled={isRunning}
        >
          <option value="">Select a cartridge...</option>
          {availableCartridges.map(cart => (
            <option key={cart} value={cart}>{cart}</option>
          ))}
        </select>
        
        <button 
          onClick={runCartridge}
          disabled={isRunning}
          style={{ marginLeft: '10px' }}
        >
          Run
        </button>
        
        {isRunning && (
          <button onClick={stopCartridge} style={{ marginLeft: '10px' }}>
            Stop
          </button>
        )}
      </div>

      {error && (
        <div style={{ color: 'red', marginBottom: '10px' }}>
          {error}
        </div>
      )}

      <div className="pico8-screen" style={{ display: 'flex', gap: '10px' }}>
        <div>
          <canvas
            ref={canvasRef}
            width={128}
            height={128}
            style={{
              border: '2px solid #000',
              imageRendering: 'pixelated',
              width: '256px',
              height: '256px',
              backgroundColor: palette[0]
            }}
          />
        </div>
        
        <div style={{ width: '200px' }}>
          <h4>Code Editor</h4>
          <textarea
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={isRunning}
            style={{
              width: '100%',
              height: '200px',
              fontFamily: 'monospace',
              fontSize: '10px',
              backgroundColor: '#000',
              color: '#fff',
              border: '1px solid #333'
            }}
          />
        </div>
      </div>

      <div className="pico8-instructions" style={{ marginTop: '10px', fontSize: '12px' }}>
        <p><strong>Fantasy Console Controls:</strong></p>
        <p>Arrow Keys: Movement | Z: Button 1 | X: Button 2</p>
        <p>This is a simplified PICO-8 style fantasy console demo.</p>
        {!selectedCartridge && (
          <p style={{ color: '#666' }}>
            Upload PICO-8 cartridge files (.p8) to the <code>/public/roms/pico8/</code> folder.
            The demo above shows a simple moving circle game.
          </p>
        )}
      </div>
    </div>
  );
};

export default PICO8Emulator; 